import { Button, CircularProgress, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import useKothar from "context/useKothar";
import { useEffect, useRef, useState } from "react";
import { BiArrowBack, BiPlus } from "react-icons/bi";
import { BsCheckAll } from "react-icons/bs";
import { MdDelete, MdNotifications } from "react-icons/md";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getClientDetails, getFeedbackClientDetails } from "utils/followUpService";
import ClientApplications from "views/admin/Clients/ClientApplications";
import FollowupDetailDialogs from "./FollowupDetailDialogs";
import SendIntakeFormDialog from "components/Modals/SendIntakeFormDialog";
import { getDiscussionHistory } from "utils/followUpService";
import { getOriginalFileName } from "utils/utils";
import { formatDisplayDate } from "utils/dateFormat";
import { getFileIcon } from "utils/utils";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { deleteDiscussionComment } from "utils/followUpService";
import AttachmentItem from "components/Attachments/AttachmentItem";

const DiscussionFile = ({ url }) => (
  <div className="flex items-center gap-1 text-xs opacity-80">
    {getFileIcon(url)}
    <Link to={url} target="_blank">
      {getOriginalFileName(url)}
    </Link>
    <Tooltip title="Download">
      <Link
        to={url}
        download
        target="_blank"
        className="text-app-text-secondary hover:text-primary"
      >
        <FileDownloadIcon className="text-sm text-white" />
      </Link>
    </Tooltip>
  </div>
);

const TAB_APPLICATIONS = "applications";
const TAB_DISCUSSION = "discussion";

const FollowupDetailView = ({ onBack }) => {
  const [mainTab, setMainTab] = useState(TAB_DISCUSSION);
  const navigate = useNavigate();

  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [selectedDiscussionId, setSelectedDiscussionId] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  // Add note dialog
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [openIntakeFormDialog, setOpenIntakeFormDialog] = useState(false);
  const [{ usersList: employees, isUsersListLoading: employeesLoading }] = useKothar();
  const userId = JSON.parse(localStorage.getItem("userDetail"))?.id;

  const { clientId, followUpId } = useParams();

  const {
    data: discussions,
    isLoading: discussionsLoading,
    refetch: refetchDiscussions,
  } = useQuery({
    queryKey: ["discussions", followUpId],
    queryFn: () => getDiscussionHistory(followUpId),
    enabled: !!followUpId,
  });

  useEffect(() => {
    if (discussions) {
      refetchDiscussions();
    }
  }, [followUpId]);

  const messagesEndRef = useRef(null);

  const {
    data: clientDetails,
    isLoading: clientLoading,
    refetch: refetchClient,
  } = useQuery({
    queryKey: ["clientDetails", clientId],
    queryFn: () => getClientDetails(clientId),
    enabled: !!clientId,
  });

  const { data: feedbackClientDetails, isLoading: feedbackClientDetailsLoading } = useQuery({
    queryKey: ["feedbackClientDetails", followUpId],
    queryFn: () => getFeedbackClientDetails(followUpId),
  });

  const { mutate: deleteDiscussionMutation, isPending: isDeletingDiscussion } = useMutation({
    mutationFn: (discussionId) => deleteDiscussionComment(followUpId, discussionId),
    onSuccess: () => {
      toast.success("Note deleted successfully");
      refetchDiscussions();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error deleting note");
    },
  });

  // Initialize form fields when client details load
  useEffect(() => {
    if (clientDetails) {
    }
  }, [clientDetails]);

  useEffect(() => {
    scrollToBottom();
  }, [discussions]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNotifyClick = (discussionId) => {
    setSelectedDiscussionId(discussionId);
    setNotifyDialogOpen(true);
  };

  const handleSendReviewForm = async () => {
    setOpenIntakeFormDialog(true);
    // try {
    //   // TODO: Uncomment API call when backend is ready
    //   // await sendReviewFormAPI(clientId);
    //   toast.success("Review form sent to client successfully (mock - not sent)");
    // } catch (error) {
    //   toast.error(error?.response?.data?.errorMessage || "Error sending review form");
    // }
  };

  const handleDeleteNote = (discussionId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) {
      return;
    }
    deleteDiscussionMutation(discussionId);
  };

  if (clientLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-app-surface-muted/60">
        <CircularProgress />
      </div>
    );
  }

  if (!clientDetails) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-app-surface-muted/60">
        <div className="text-red-600 p-4 bg-red-50 rounded-xl border border-red-100">
          Client not found
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-surface-muted/60">
      {/* Header */}
      <div className="bg-app-surface border-b border-app-border/80 sticky top-0 z-50 shadow-sm flex items-center justify-between">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-3 ">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-2 py-1.5 text-app-text-secondary hover:text-app-text hover:bg-app-surface-muted rounded-lg transition-colors text-sm font-medium"
          >
            <BiArrowBack size={20} />
          </button>
          <span className="text-slate-300">·</span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-app-text truncate">
              {feedbackClientDetails?.client?.name || "Client"}
            </h1>
            <p className="text-sm text-app-text-muted">Client feedback & management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setAddNoteDialogOpen(true);
            }}
            startIcon={<BiPlus size={18} />}
          >
            Add note
          </Button>
          <Button variant="outlined" color="primary" onClick={handleSendReviewForm}>
            Send review form
          </Button>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left sidebar – scrollable on small screens */}
          <div className="lg:col-span-1 space-y-5 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-1">
            {/* Overview card – compact client + visa + passport */}
            <div className="bg-app-surface rounded-xl border border-app-border/80 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-app-border">
                <h3 className="text-sm font-semibold text-app-text">Overview</h3>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-sm font-medium text-app-text mt-0.5">
                    {feedbackClientDetails?.client?.name}
                  </p>
                  <p className="text-sm text-app-text mt-0.5 break-all">
                    {feedbackClientDetails?.client?.email} | {feedbackClientDetails?.client?.number}
                  </p>
                </div>

                <div className="pt-3 border-t border-app-border grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Visa</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.client?.visaStatus || "—"}
                    </p>
                    <p
                      className={`text-xs mt-0.5 font-medium ${
                        feedbackClientDetails?.client?.visaExpiry
                          ? new Date(feedbackClientDetails?.client?.visaExpiry) < new Date()
                            ? "text-red-600"
                            : "text-emerald-600"
                          : "text-app-text-muted"
                      }`}
                    >
                      {feedbackClientDetails?.client?.visaExpiry
                        ? formatDisplayDate(feedbackClientDetails?.client?.visaExpiry)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Passport</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.client?.passportNumber || "—"}
                    </p>
                    <p
                      className={`text-xs mt-0.5 font-medium ${
                        feedbackClientDetails?.client?.passportExpiry
                          ? new Date(feedbackClientDetails?.client?.passportExpiry) < new Date()
                            ? "text-red-600"
                            : "text-emerald-600"
                          : "text-app-text-muted"
                      }`}
                    >
                      {clientDetails?.passportExpiry
                        ? formatDisplayDate(clientDetails.passportExpiry)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Status</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.status || "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Priority</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.priority || "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Created by</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.createdByName || "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Created Date</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.createdDate
                        ? formatDisplayDate(feedbackClientDetails?.createdDate)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Updated By</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.updatedByName
                        ? feedbackClientDetails?.updatedByName
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-app-text-muted">Updated Date</span>
                    <p className="text-sm text-app-text mt-0.5">
                      {feedbackClientDetails?.updatedDate
                        ? formatDisplayDate(feedbackClientDetails?.updatedDate)
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main content: Applications + Discussion tabs */}
          <div className="lg:col-span-2 flex flex-col min-h-[500px] lg:min-h-[calc(100vh-15rem)]">
            <div className="bg-app-surface rounded-xl border border-app-border/80 shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {/* Tab bar */}
              <div className="flex border-b border-app-border/80 bg-app-surface-muted/50 px-1">
                <button
                  type="button"
                  onClick={() => setMainTab(TAB_DISCUSSION)}
                  className={`px-5 py-3.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${
                    mainTab === TAB_DISCUSSION
                      ? "bg-app-surface text-app-text shadow-sm border border-app-border/80 border-b-0 -mb-px"
                      : "text-app-text-secondary hover:text-app-text"
                  }`}
                >
                  Discussion
                  {discussions?.comments?.length > 0 && (
                    <span className="bg-app-surface-hover text-app-text-secondary text-xs font-semibold px-1.5 py-0.5 rounded-full">
                      {discussions?.comments?.length}
                    </span>
                  )}
                </button>{" "}
                <button
                  type="button"
                  onClick={() => setMainTab(TAB_APPLICATIONS)}
                  className={`px-5 py-3.5 text-sm font-medium rounded-t-lg transition-colors ${
                    mainTab === TAB_APPLICATIONS
                      ? "bg-app-surface text-app-text shadow-sm border border-app-border/80 border-b-0 -mb-px"
                      : "text-app-text-secondary hover:text-app-text"
                  }`}
                >
                  Applications
                </button>
              </div>
              {/* Tab content */}
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {mainTab === TAB_DISCUSSION && (
                  <>
                    <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-app-surface-muted/50 min-h-0">
                      <div className="flex items-center justify-between gap-4 mb-2">
                        <p className="text-xs text-app-text-muted">
                          {discussions?.comments?.length} message
                          {discussions?.comments?.length !== 1 ? "s" : ""}
                        </p>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setAddNoteDialogOpen(true);
                          }}
                          startIcon={<BiPlus size={18} />}
                        >
                          Add note
                        </Button>
                      </div>
                      {discussions?.comments?.length === 0 ? (
                        <div className="flex items-center justify-center h-full min-h-[180px] text-center">
                          <div className="text-app-text-muted">
                            <p className="font-medium text-app-text-secondary">No notes yet</p>
                            <p className="text-sm mt-1">
                              Click &quot;Add note&quot; to log progress or updates.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="h-[60vh] overflow-y-auto space-y-4">
                          {discussions?.comments
                            ?.sort(
                              (a, b) =>
                                new Date(b.createdDate).getTime() -
                                new Date(a.createdDate).getTime()
                            )
                            ?.reverse()
                            ?.map((discussion, index) => {
                              const isCurrentUser = discussion.commentedById === userId;
                              return (
                                <div
                                  key={discussion.id || index}
                                  className={`flex gap-2.5 ${
                                    isCurrentUser ? "justify-end" : "justify-start"
                                  }`}
                                >
                                  {!isCurrentUser && (
                                    <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0">
                                      <span className="text-red font-medium text-xs uppercase">
                                        {discussion.createdBy?.[0]?.toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                  <div
                                    className={`max-w-[85%] sm:max-w-sm px-4 py-2.5 rounded-xl text-sm shadow-sm ${
                                      isCurrentUser
                                        ? "bg-primary text-white rounded-br-md"
                                        : "bg-app-surface border border-app-border text-primary rounded-bl-md"
                                    } relative group`}
                                  >
                                    {!isCurrentUser && (
                                      <p className="text-xs font-medium text-app-text-muted mb-1">
                                        {discussion.createdBy || "Team"}
                                      </p>
                                    )}
                                    <p className="leading-relaxed">{discussion.text}</p>
                                    {discussion.attachment && (
                                      <div className="mt-1.5">
                                        <AttachmentItem
                                          url={discussion.attachment}
                                          renderFile={(url) => <DiscussionFile url={url} />}
                                        />
                                      </div>
                                    )}

                                    <div className="mt-2 pt-2 border-t border-app-border/40 space-y-1.5 text-xs">
                                      {discussion.createdDate && (
                                        <div className="flex justify-between gap-4">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text"
                                            }
                                          >
                                            Contacted
                                          </span>
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text"
                                            }
                                          >
                                            {formatDisplayDate(discussion.createdDate)}{" "}
                                            {new Date(discussion.createdDate).toLocaleTimeString()}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.nextFollowupDate && (
                                        <div className="flex justify-between gap-4">
                                          <span className="text-app-text-muted">
                                            Next follow-up
                                          </span>
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text"
                                            }
                                          >
                                            {formatDisplayDate(discussion.nextFollowupDate)}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.isInterested && (
                                        <div className="flex justify-between gap-4">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text-muted"
                                            }
                                          >
                                            Interest
                                          </span>
                                          <span
                                            className={
                                              isCurrentUser
                                                ? "text-white"
                                                : "text-text text-right capitalize"
                                            }
                                          >
                                            {discussion.isInterested}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.interestedService && (
                                        <div className="flex justify-between gap-4">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-primary"
                                            }
                                          >
                                            Service
                                          </span>
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-text text-right"
                                            }
                                          >
                                            {discussion.interestedService}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.currentStatus && (
                                        <div className="flex justify-between gap-4">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text-muted"
                                            }
                                          >
                                            Current status
                                          </span>
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-text text-right"
                                            }
                                          >
                                            {discussion.currentStatus}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.assignedCounsellorName && (
                                        <div className="flex justify-between gap-4">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text-muted"
                                            }
                                          >
                                            Assigned counsellor
                                          </span>
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-text text-right"
                                            }
                                          >
                                            {discussion.assignedCounsellorName}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.happyWithService && (
                                        <div className="flex justify-between gap-4">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text-muted"
                                            }
                                          >
                                            Happy with service
                                          </span>
                                          <span
                                            className={`${
                                              isCurrentUser
                                                ? "text-white"
                                                : "text-right font-medium"
                                            } ${
                                              discussion.happyWithService === "no"
                                                ? "text-amber-300"
                                                : "text-emerald-300"
                                            }`}
                                          >
                                            {discussion.happyWithService === "yes" ? "Yes" : "No"}
                                          </span>
                                        </div>
                                      )}
                                      {discussion.badServiceReason && (
                                        <div className="flex flex-col gap-0.5">
                                          <span
                                            className={
                                              isCurrentUser ? "text-white" : "text-app-text-muted"
                                            }
                                          >
                                            Reason
                                          </span>
                                          <span
                                            className={isCurrentUser ? "text-white" : "text-text"}
                                          >
                                            {discussion.badServiceReason}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleDeleteNote(discussion.id)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity absolute -left-2 -top-2 px-2 py-1 bg-red-500 text-white rounded-lg text-xs font-medium shadow flex items-center gap-1"
                                    >
                                      <MdDelete size={12} />
                                      Delete Note
                                    </button>
                                    <button
                                      onClick={() => handleNotifyClick(discussion.id)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity absolute -right-2 -top-2 px-2 py-1 bg-app-surface border border-app-border hover:bg-app-surface-muted rounded-lg text-xs text-app-text-secondary font-medium shadow flex items-center gap-1"
                                    >
                                      <MdNotifications size={12} />
                                      Notify
                                    </button>
                                  </div>
                                  {discussion.commentedById === userId && (
                                    <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                                      <span className="text-white font-medium text-xs">
                                        {discussion.createdBy?.[0]?.toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </div>
                  </>
                )}
                {mainTab === TAB_APPLICATIONS && (
                  <div className="flex-1 overflow-auto p-4 min-h-0">
                    <ClientApplications clientId={clientId} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <FollowupDetailDialogs
        notifyDialogOpen={notifyDialogOpen}
        setNotifyDialogOpen={setNotifyDialogOpen}
        selectedDiscussionId={selectedDiscussionId}
        setSelectedDiscussionId={setSelectedDiscussionId}
        addNoteDialogOpen={addNoteDialogOpen}
        setAddNoteDialogOpen={setAddNoteDialogOpen}
        followUpId={followUpId}
        employeesLoading={employeesLoading}
        employees={employees}
        closeAddNoteDialog={() => setAddNoteDialogOpen(false)}
        refetchDiscussions={refetchDiscussions}
      />
      <SendIntakeFormDialog
        open={openIntakeFormDialog}
        setOpen={setOpenIntakeFormDialog}
        clientId={clientId}
      />
    </div>
  );
};

export default FollowupDetailView;
